import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';

import usePlannerStore from '../../store';

/**
 * Calculates the visible date range in the planner viewport.
 * Used for selective query invalidation to avoid refetching non-visible data.
 *
 * @returns Object with startDate and endDate in YYYY-MM-DD format, or null if cannot calculate
 */
export function getVisibleDateRange(): { startDate: string; endDate: string } | null {
  const firstVisibleDate = usePlannerStore.getState().firstVisibleDate;

  if (!firstVisibleDate) {
    return null;
  }

  // Calculate visible range: from first visible date to ~2 months ahead
  // This ensures we cover the entire viewport plus a small buffer
  const startDate = firstVisibleDate.format(DATE_KEY_FORMAT);
  const endDate = firstVisibleDate.add(2, 'months').format(DATE_KEY_FORMAT);

  return { startDate, endDate };
}

/**
 * Checks if a date range overlaps with another date range.
 * Used to determine which query pages to invalidate.
 *
 * @param range1 First date range
 * @param range2 Second date range
 * @returns true if ranges overlap, false otherwise
 */
export function dateRangesOverlap(
  range1: { startDate: string; endDate: string },
  range2: { startDate: string; endDate: string }
): boolean {
  const start1 = dayjs(range1.startDate);
  const end1 = dayjs(range1.endDate);
  const start2 = dayjs(range2.startDate);
  const end2 = dayjs(range2.endDate);

  // Ranges overlap if: start1 <= end2 AND start2 <= end1
  return start1.isSameOrBefore(end2) && start2.isSameOrBefore(end1);
}
